// hooks/useGameWs.js: M4-10 Preact hook for the networked game.
//
// Subscribes to a shared room-ws service (the one M3-13b created) and
// reduces game events into a per-game view. The room hook still owns
// the room state; this hook only sees events whose type matches a
// game-layer event AND the COMMAND_REJECTED frames the server fans out
// when a game command fails.
//
// Surface (returned from the hook):
//   - view: selectGameView(...) output, gameState, myHand, isMyTurn,
//     currentState, timer deadlines, gameOver, errorToast.
//   - sendGameCommand(command): wraps service.sendGameCommand.
//   - dismissError(): clears the current toast.
//
// The hook does NOT subscribe to room events, that's useRoomWs's job.
// Callers pass the room-ws service (or a mock for testing).

import { useReducer, useEffect, useCallback, useMemo, useRef, useState } from 'preact/hooks';
import { gameReducer, INITIAL_GAME_STATE, selectGameView } from '../game/game-reducer.js';
import { createOccupantSeatTracker } from '../game/occupant-seats.js';

const GAME_EVENT_TYPES = new Set([
  'GAME_INITIALIZED', 'BID_SUBMITTED', 'DEALER_DISCARDED',
  'CARD_PLAYED', 'TRICK_RESOLVED', 'HAND_SCORED',
  'BID_TIMER_STARTED', 'PLAY_TIMER_STARTED',
  'BID_TIMEOUT', 'PLAY_TIMEOUT',
  'HAND_PASSED_OUT', 'HAND_READY_ADVANCED',
  'SEAT_RECLAIMED', 'BOT_SUBSTITUTED', 'GAME_ENDED',
  'COMMAND_REJECTED',
]);

/**
 * @param {{
 *   service: any,
 *   onEvent?: (event: any) => void,
 *   mySeatIndex: 0|1|2|3|null,
 *   roomOccupants?: any[],
 * }} opts
 */
export function useGameWs(opts) {
  const { service, mySeatIndex: mySeatIndexProp, onEvent, roomOccupants = [] } = opts;
  // Item 4: occupant → Lua seat from the game's own events, with the
  // room list as a fill-in. It keeps leavers, and never matches by name.
  /** @type {{ current: ReturnType<typeof createOccupantSeatTracker>|null }} */
  const seatsRef = useRef(null);
  if (seatsRef.current === null) seatsRef.current = createOccupantSeatTracker();
  seatsRef.current.learnFromRoom(roomOccupants);
  /** @type {{ current: string|null }} */
  const selfOccupantRef = useRef(null);
  const [state, dispatch] = useReducer(gameReducer, INITIAL_GAME_STATE);
  // M4-11: GAME_INITIALIZED carries `your_seat_index` for the recipient.
  // We trust that over the room-layer prop so the seat is known the
  // instant the first snapshot arrives, even before the room snapshot
  // catches up.
  const [seatFromGame, setSeatFromGame] = useReducer((_, next) => next, null);

  useEffect(() => {
    if (!service || typeof service._subscribeForGame !== 'function') return undefined;
    const unsub = service._subscribeForGame((ev) => {
      if (!ev || typeof ev !== 'object') return;
      const seats = /** @type {ReturnType<typeof createOccupantSeatTracker>} */ (seatsRef.current);
      // A room-level departure during the game: the seat stays a bot's
      // for good, and its plate keeps the mark (item 4).
      if (ev.type === 'OCCUPANT_LEFT') {
        const id = ev.payload?.occupant_id;
        const seat = seats.seatOf(id);
        if (seat != null && id !== selfOccupantRef.current) {
          dispatch({ type: 'SEAT_DEPARTED', payload: { actor_seat: seat, reason: ev.payload?.reason ?? null } });
        }
        return;
      }
      if (GAME_EVENT_TYPES.has(ev.type)) {
        seats.learnFromGameEvent(ev);
        if (ev.type === 'GAME_INITIALIZED' && typeof ev.payload?.your_seat_index === 'number') {
          setSeatFromGame(ev.payload.your_seat_index);
          selfOccupantRef.current = ev.payload?.your_occupant_id ?? selfOccupantRef.current;
        }
        // Item 2 (round 4): after a reattach the client cannot know if its
        // ready reached the server, so Continue works again. A second
        // press is safe: the server answers HAND_ALREADY_ADVANCING.
        if (ev.type === 'GAME_INITIALIZED' && ev.payload?.reattach) setReady(null);
        const needsSeat = (ev.type === 'BOT_SUBSTITUTED' || ev.type === 'SEAT_RECLAIMED')
          && ev.payload?.actor_seat == null;
        const actorSeat = needsSeat ? seats.seatOf(ev.payload?.occupant_id) : null;
        if (needsSeat && actorSeat == null) {
          console.warn(`[useGameWs] ${ev.type}: no seat known for occupant ${ev.payload?.occupant_id}; the log names "Someone"`);
        }
        dispatch(actorSeat != null
          ? { ...ev, payload: { ...ev.payload, actor_seat: actorSeat } }
          : ev);
        if (onEvent) try { onEvent(ev); } catch (e) { console.warn('[useGameWs] onEvent listener threw:', e); }
      }
    });
    return unsub;
  }, [service, onEvent]);

  // Item 1 (round 3): a move that waits for its answer. The view shows
  // "Sending…" and locks the bid, play and discard controls meanwhile.
  const [sending, setSending] = useState(false);
  // Item 2 (round 4): the ready (Continue, Back to lobby) and its answer.
  const [ready, setReady] = useState(/** @type {ReadyState} */ (null));
  useEffect(() => {
    if (!service || typeof service.subscribePendingCommand !== 'function') return undefined;
    return service.subscribePendingCommand((pending, settled) => {
      setSending(pending != null);
      if (settled) setReady((r) => readyAfterSettle(r, settled));
    });
  }, [service]);

  const mySeatIndex = seatFromGame != null ? seatFromGame : (mySeatIndexProp ?? null);

  const sendGameCommand = useCallback((cmd) => {
    if (!service) return null;
    return service.sendGameCommand(cmd);
  }, [service]);

  const markHandReady = useCallback(() => {
    if (!service) return null;
    const sent = service.sendGameCommand({ type: 'MARK_HAND_READY', payload: {} });
    if (sent) setReady({ key: sent.idempotency_key, phase: 'sending' });
    return sent;
  }, [service]);

  const clearReady = useCallback(() => setReady(null), []);

  const dismissError = useCallback(() => {
    dispatch({ type: 'CLEAR_TOAST' }); // no-op in reducer; toast clears on next event
  }, []);

  const view = useMemo(() => selectGameView(state, /** @type {0|1|2|3|null} */ (mySeatIndex)), [state, mySeatIndex]);

  return {
    view,
    sendGameCommand,
    dismissError,
    /** True while a sent move waits for the server's answer. */
    sending,
    /**
     * The ready for this recap: 'idle' (Continue works), 'sending' (no
     * answer yet) or 'waiting' (the server has it; waiting for the others).
     */
    readyState: /** @type {'idle'|'sending'|'waiting'} */ (ready ? ready.phase : 'idle'),
    markHandReady,
    /** Forget the ready, when the hand (or the game) moves on. */
    clearReady,
    /**
     * The recipient's seat index (0..3) as authoritative as we know it,
     * either the GAME_INITIALIZED payload (`your_seat_index`) or the
     * room-layer prop fallback. Components should prefer this over the
     * raw prop so dealer-discard / hit-zone checks line up with the
     * snapshot's `current_turn_seat`.
     */
    mySeatIndex: /** @type {0|1|2|3|null} */ (mySeatIndex),
    _state: state,
  };
}

/** @typedef {{ key: string, phase: 'sending'|'waiting' }|null} ReadyState */

/**
 * The ready after the service ends the wait for a command. COMMAND_ACCEPTED,
 * the game event the ready caused, or HAND_ALREADY_ADVANCING (the server
 * already has this seat's ready) mean the server has it. Any other answer,
 * or no answer (a reattach, a reset), means Continue works again.
 *
 * @param {ReadyState} ready
 * @param {{ command: { type: string, idempotency_key: string }, answer: any }} settled
 * @returns {ReadyState}
 */
export function readyAfterSettle(ready, settled) {
  if (!ready || settled.command.idempotency_key !== ready.key) return ready;
  const answer = settled.answer;
  if (!answer) return null;
  if (answer.type === 'COMMAND_REJECTED') {
    return answer.payload?.error?.code === 'HAND_ALREADY_ADVANCING' ? { ...ready, phase: 'waiting' } : null;
  }
  return { ...ready, phase: 'waiting' };
}

/**
 * Test-seam factory: build a reducer-only "game state" object without
 * Preact. Use in unit tests that drive event sequences directly.
 *
 * @param {0|1|2|3|null} mySeatIndex
 */
export function createGameStateMachine(mySeatIndex = null) {
  let state = INITIAL_GAME_STATE;
  return {
    apply(event) { state = gameReducer(state, event); return state; },
    view() { return selectGameView(state, mySeatIndex); },
    get state() { return state; },
  };
}
